import {CommandBase} from "./commandBase";
import {RemoteConstantsModel, TechEdgePoint} from "../models/remoteConstants.model";
import {removeEdgeRoute} from "../utils/techTree.utils";

// Replaces one dependency edge's authored bends outright. Like the node-move
// command this is dispatched once per completed gesture, never per pointer-move,
// and returns false when nothing actually changed so a stray click costs no
// history entry.
//
// An empty point list removes the route record entirely rather than storing an
// empty one, which keeps a straightened edge indistinguishable from an edge that
// was never bent.
export class SetTechEdgeRouteCommand extends CommandBase {
    parent: string;
    child: string;
    points: TechEdgePoint[];
    endAxis?: 'h' | 'v';

    constructor(parent: string, child: string, points: TechEdgePoint[], endAxis?: 'h' | 'v') {
        super();
        this.parent = parent;
        this.child = child;
        this.points = points;
        this.endAxis = endAxis;
    }

    execute(data: RemoteConstantsModel): boolean {
        const node = data.techTree.find(t => t.id === this.parent);
        if (node === undefined || !node.children.includes(this.child)) {
            return false;
        }

        const existing = node.routes?.find(r => r.child === this.child);
        if (samePoints(existing?.points ?? [], this.points)
            && (existing?.endAxis ?? undefined) === this.endAxis) {
            return false;
        }

        if (this.points.length === 0) {
            removeEdgeRoute(node, this.child);
            return true;
        }

        const route = {
            child: this.child,
            points: this.points,
            ...(this.endAxis ? {endAxis: this.endAxis} : {})
        };

        if (existing === undefined) {
            node.routes = [...(node.routes ?? []), route];
        } else {
            node.routes = node.routes!.map(r => r.child === this.child ? route : r);
        }

        return true;
    }
}

function samePoints(a: TechEdgePoint[], b: TechEdgePoint[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i].x !== b[i].x || a[i].y !== b[i].y) return false;
        if ((a[i].axis ?? 'h') !== (b[i].axis ?? 'h')) return false;
    }
    return true;
}
